import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Text from './Text';

const ITEM_HEIGHT = 34;
const ITEM_GAP = 6;
const VISIBLE_ITEMS = 5;
const CENTER_INDEX = Math.floor(VISIBLE_ITEMS / 2);
const SCROLL_THRESHOLD = 10;

const Container = styled.div`
  width: 100px;
  height: 180px;
  position: relative;
  perspective: 800px;
  overflow: hidden;
`;
const WheelList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  transform-style: preserve-3d;
  // cursor: ${(props) => (props.isDragging ? 'grabbing' : 'grab')};
  user-select: none;
  z-index: 100;
`;
const ListItem = styled.div`
  margin-top: ${ITEM_GAP / 2}px;
  margin-bottom: ${ITEM_GAP / 2}px;
  height: ${ITEM_HEIGHT}px;
  color: ${(props) => props.theme.colors.primary};
  font-size: 1.5rem;
  text-align: center;
  line-height: ${ITEM_HEIGHT}px;
  transform: ${({ $index, $selectedIndex }) => {
    const distance = $index - $selectedIndex;
    const rotation = distance * -20;
    return `rotateX(${rotation}deg)`;
  }};
  
  // 중앙에서 멀어질수록 점점 흐리게 만들기
  opacity: ${({ $index, $selectedIndex }) => {
    const distance = Math.abs($index - $selectedIndex);
    return Math.max(1 - distance * 0.4, 0.4); 
  }};
`;

const SelectWheel = ({ list, onChange, selected, isLoop }) => {
  const totalItems = list.length;
  const extendedList = isLoop ? [...list, ...list, ...list] : list; // 무한 루프 여부 체크

  // 부모의 selected 값이 변경되면 자동으로 반영되도록 useEffect 추가
  const [selectedItemIndex, setSelectedItemIndex] = useState(
    list.indexOf(selected) + (isLoop ? totalItems : 0)
  );

  useEffect(() => {
    setSelectedItemIndex(list.indexOf(selected) + (isLoop ? totalItems : 0));
  }, [selected, list, totalItems, isLoop]); 

  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);
  const animationFrame = useRef(null);

  const handleDragStart = (event) => {
    startY.current = event.clientY || event.touches[0].clientY;
    currentY.current = startY.current;
    isDragging.current = true;
  };

  const handleDragMove = (event) => {
    if (!isDragging.current) return;

    const clientY = event.clientY || event.touches[0].clientY;
    const deltaY = clientY - currentY.current;

    if (Math.abs(deltaY) < SCROLL_THRESHOLD) return;

    let newIndex = selectedItemIndex - Math.sign(deltaY);

    if (isLoop) {
      if (newIndex < totalItems) newIndex += totalItems;
      else if (newIndex >= totalItems * 2) newIndex -= totalItems;
    } else {
      // isLoop=false인 경우 (오전/오후) 리스트의 시작과 끝을 벗어나지 않도록 제한
      newIndex = Math.max(0, Math.min(newIndex, totalItems - 1));
    }

    currentY.current = clientY;

    if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    animationFrame.current = requestAnimationFrame(() => {
      setSelectedItemIndex(newIndex);
    });
  };

  const handleDragEnd = () => {
    isDragging.current = false;
    if (onChange) {
      onChange(list[selectedItemIndex % totalItems]);
    }
  };

  return (
    <Container
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}>
      <WheelList 
        style={{  transform: `translateY(${CENTER_INDEX * ITEM_HEIGHT + (CENTER_INDEX - 1) * ITEM_GAP / 2 - selectedItemIndex * (ITEM_HEIGHT + ITEM_GAP)}px)` }}>
        {extendedList.map((item, index) => (
          <ListItem key={`${item}-${index}`} $index={index} $selectedIndex={selectedItemIndex}>
            <Text variant="headline2" weight="medium">{item}</Text>
          </ListItem>
        ))}
      </WheelList>
    </Container>
  );
};

export default SelectWheel;
